import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, BookMarked, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { pageHead } from "@/lib/seo";

type Shelf = {
  id: string;
  user_id: string;
  name: string;
  emoji: string | null;
  description: string | null;
  book_count?: number;
  owner_name?: string | null;
};

const EMOJIS = ["📚", "🌙", "🎈", "🦄", "🌟", "🐻", "🚀", "🌸", "🎨", "🧙"];

export const Route = createFileRoute("/shelves")({
  head: () => pageHead({
    path: "/shelves",
    title: "Family shelves — Book Swap",
    description: "Browse curated family shelves members share publicly, and build your own themed shelf of books to lend or swap.",
  }),
  component: Shelves,
});

function Shelves() {
  const { user } = useAuth();
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", emoji: "📚", description: "" });
  const [saving, setSaving] = useState(false);

  async function load() {
    const { data: rows } = await supabase.from("shelves").select("*").order("created_at", { ascending: false }).limit(60);
    if (!rows) return setShelves([]);
    const ids = rows.map((s) => s.id);
    const ownerIds = Array.from(new Set(rows.map((s) => s.user_id)));
    const [{ data: counts }, { data: owners }] = await Promise.all([
      ids.length
        ? supabase.from("shelf_books").select("shelf_id").in("shelf_id", ids)
        : Promise.resolve({ data: [] as any[] }),
      ownerIds.length
        ? supabase.from("public_profiles").select("id, full_name").in("id", ownerIds)
        : Promise.resolve({ data: [] as any[] }),
    ]);
    const countMap = new Map<string, number>();
    (counts ?? []).forEach((r: any) => countMap.set(r.shelf_id, (countMap.get(r.shelf_id) ?? 0) + 1));
    const ownerMap = new Map<string, string | null>();
    (owners ?? []).forEach((o: any) => ownerMap.set(o.id, o.full_name));
    setShelves(rows.map((s: any) => ({ ...s, book_count: countMap.get(s.id) ?? 0, owner_name: ownerMap.get(s.user_id) ?? null })));
  }
  useEffect(() => { if (user) load(); }, [user]);

  async function create() {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from("shelves").insert({
      user_id: user.id,
      name: form.name,
      emoji: form.emoji,
      description: form.description || null,
    });
    setSaving(false);
    if (error) { console.error(error); return toast.error("Could not create shelf."); }
    toast.success("Shelf created!");
    setForm({ name: "", emoji: "📚", description: "" });
    setOpen(false);
    load();
  }

  async function deleteShelf(s: Shelf) {
    if (!user || s.user_id !== user.id) return;
    if (!confirm(`Delete "${s.name}"? The books on it stay listed.`)) return;
    const { error } = await supabase.from("shelves").delete().eq("id", s.id);
    if (error) { console.error(error); return toast.error("Could not delete this shelf."); }
    toast.success("Shelf deleted");
    load();
  }



  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <BookMarked className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold">Family Shelves</h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">Curated collections shared by the community.</p>
          </div>
          {user && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90"><Plus className="me-1 h-4 w-4" /> New shelf</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Create a shelf</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Bedtime Stories" /></div>
                  <div>
                    <Label>Emoji</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {EMOJIS.map((e) => (
                        <button key={e} type="button" onClick={() => setForm({ ...form, emoji: e })}
                          className={`flex h-10 w-10 items-center justify-center rounded-lg text-xl ${form.emoji === e ? "bg-primary text-white" : "bg-muted"}`}>
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                  <Button disabled={!form.name.trim() || saving} onClick={create} className="w-full bg-primary hover:bg-primary/90">
                    {saving ? "Creating…" : "Create shelf"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {shelves.length === 0 ? (
          <div className="mt-12 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
            <p className="text-sm text-muted-foreground">No shelves yet. Create the first one!</p>
          </div>
        ) : (
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {shelves.map((s) => (
              <div key={s.id} className="relative rounded-2xl bg-card p-5 shadow-[var(--shadow-card)] transition hover:shadow-lg">
                {user?.id === s.user_id && (
                  <button
                    type="button"
                    onClick={() => deleteShelf(s)}
                    aria-label="Delete shelf"
                    className="absolute end-3 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <Link to="/shelves/$id" params={{ id: s.id }} className="block">
                  <div className="flex items-start gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-2xl">{s.emoji ?? "📚"}</div>
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate font-semibold">{s.name}</h2>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        by {s.owner_name ?? "Anonymous"} · {s.book_count} {s.book_count === 1 ? "book" : "books"}
                      </p>
                    </div>
                  </div>
                  {s.description && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{s.description}</p>}
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
